import React, { useCallback, useContext, useState } from 'react'
import cx from 'classnames'
import * as styles from '../styles/components/brand-logo.module.scss'
import LogoFrames from '../assets/images/Koherent-black-frames.svg'
import Logo from '../assets/images/Koherent-black.svg'
import LogoFull from '../assets/images/Koherent-black-full.svg'
import PageLoadContext from '../context/pageLoadContext'

type LogoVersion = 'expanded' | 'contracted'
type ColorVariant = 'light' | 'dark'

export interface BrandLogoProps {
    animated?: boolean
    animateOnHover?: boolean
    logoVersion?: LogoVersion
    colorVariant?: ColorVariant
    className?: string
}

interface LogoProps extends React.SVGProps<SVGSVGElement> {
    version: LogoVersion
    color: ColorVariant
}

// The logo is animated by having all frames side-by-side in a single SVG as in
//  KOHERENT  KOHER|NT  KOH|R|NT  K|H|R|NT
// The SVG is then shifted sideways in discrete steps within a limited width container (with overflow hidden) to create the animation

export const BrandLogo = (props: BrandLogoProps) => {
    const { className, animated, colorVariant = 'light', logoVersion = 'contracted', animateOnHover } = props
    const [hasBeenHovered, setHasBeenHovered] = useState(false)
    const pageHasLoaded = useContext(PageLoadContext)

    // Used for applying animation delay only on initial load but not when hovered
    const hoverHandler = useCallback(() => {
        setHasBeenHovered(true)
    }, [setHasBeenHovered])

    return (
        <div
            className={cx(styles.container, {
                [styles.withHoverEffect]: animateOnHover,
                [styles.withAnimationDelay]: !hasBeenHovered,
            })}
            onMouseEnter={animateOnHover ? hoverHandler : undefined}
        >
            { animated
                ? <LogoFrames
                    className={cx(styles.animated, styles[logoVersion], styles[colorVariant], className, {
                        [styles.animationPaused]: !pageHasLoaded,
                    })}
                />
                : <LogoBasedOnProps
                    version={logoVersion}
                    color={colorVariant}
                    className={cx(styles.staticLogo, styles[colorVariant], className)}
                />
            }
        </div>
    )
}

const LogoBasedOnProps = ({ version, color, ...rest }: LogoProps) => {
    return version === 'expanded'
        ? <LogoFull {...rest}/>
        : <Logo {...rest} />
}

export default BrandLogo
